import mixpanel from "mixpanel-browser";
import { defineStore, storeToRefs } from "pinia";
import { computed, watch } from "vue";
import { ref } from "vue";
import { useRouter } from "vue-router";

import { useOrganizationManager } from "@/composables/organization-manager";
import { type types, useClient, useServices } from "@/lib/openapi";
// Import enum directly as index only exports as types.
import { ClusterRole as ClusterRoleEnum } from "@/lib/openapi/gen/types.gen";

import {
    AuthStatus,
    useAuthenticationManager,
    useAuthenticationStore,
} from "./authentication-store";

export type MeUser = types.MeUser;
export type ClusterRole = types.ClusterRole;
export type UserPermissions = types.UserPermissions;
// Ignore @typescript-eslint/no-redeclare.
// eslint-disable-next-line
export const ClusterRole = ClusterRoleEnum;

export const useUserStore = defineStore("user", () => {
    const { organization } = useOrganizationManager();
    const { signOut } = useAuthenticationStore();
    const { authStatus, authUser } = useAuthenticationManager();
    const router = useRouter();
    const user = ref<MeUser>();
    const currentOrganizationRoles = ref<ClusterRole[]>([]);
    const currentOrganizationPermissions = ref<UserPermissions>();
    const controller = ref(new AbortController());

    const api = useServices({
        opts: {
            throwOnError: false,
        },
        requires: {
            auth: true,
            organization: false,
            stek: false,
        },
    });

    const preferencesClient = useClient({
        requires: { auth: true, organization: false, stek: false },
    });

    const updatePhotoUrlFromAuthData = async () => {
        try {
            await api.updateSelfUserMePut({
                body: {
                    profile_picture: authUser.value.photoUrl,
                },
            });
        } catch (error) {
            console.error(
                "Failed to update profile picture in backend:",
                error
            );
        }
    };

    async function updateUser() {
        controller.value.abort();
        controller.value = new AbortController();

        try {
            const { response, data } = await api.getSelfUserMeGet({
                signal: controller.value.signal,
            });
            if (!response) {
                // Request was aborted or failed before getting a response
                // This is expected during org/auth transitions - silently return
                return;
            } else if (!response.ok) {
                if (response.status === 401) {
                    router.push({ name: "home" });
                    await signOut({
                        severity: "error",
                        summary: "Incorrect login.",
                    });
                    reset();
                } else {
                    throw new Error(
                        response.statusText || "Failed to get user info"
                    );
                }
            } else {
                user.value = data;

                if (
                    authUser.value?.photoUrl &&
                    authUser.value.photoUrl !== user.value?.profile_picture
                ) {
                    updatePhotoUrlFromAuthData();
                }
                for (const [orgId, roles] of Object.entries(
                    user.value!.roles
                )) {
                    if (orgId === organization.value?.identity) {
                        currentOrganizationRoles.value = roles as ClusterRole[];
                        currentOrganizationPermissions.value =
                            user.value!.permissions[orgId];
                        break;
                    }
                }
                mixpanel.identify(user.value?.identity);
                mixpanel.people.set_once({
                    "role": currentOrganizationRole.value,
                });
            }
        } catch (e) {
            if (e instanceof DOMException && e.name === "AbortError") {
                // Request was aborted by the AbortController.
                return;
            } else {
                throw e;
            }
        }
    }

    function reset() {
        user.value = undefined;
    }

    // When the organization changes, the permissions for the current user
    // change as well.
    watch(
        () => organization.value?.identity,
        () => {
            updateUser();
        }
    );

    watch(
        authStatus,
        (newAuthStatus) => {
            if (newAuthStatus !== AuthStatus.SIGNED_IN) {
                reset();
            } else {
                updateUser();
            }
        },
        { immediate: true }
    );

    // Backward-compat computed for callers that need a single role value (e.g. Mixpanel).
    const currentOrganizationRole = computed(
        () => currentOrganizationRoles.value[0]
    );

    const userPreferences = computed(() => user.value?.preferences ?? null);

    const hintsEnabled = computed(
        () =>
            user.value !== undefined &&
            (userPreferences.value?.hints_enabled ?? true)
    );

    const seenHints = computed(
        () => (userPreferences.value?.seen_hints ?? []) as string[]
    );

    async function patchPreferences(body: {
        hints_enabled?: boolean;
        seen_hints?: string[];
    }) {
        const result = await preferencesClient.patch<MeUser>({
            url: "/user/me/preferences",
            body,
            headers: { "Content-Type": "application/json" },
        });
        const data = (result as { data?: MeUser }).data;
        if (data && user.value) {
            user.value.preferences = data.preferences ?? null;
        }
    }

    async function markHintSeen(hintId: string) {
        if (!user.value) return;
        const current = seenHints.value;
        if (current.includes(hintId)) return;
        // Optimistic update so the UI advances immediately without waiting for the network.
        const updated = [...current, hintId];
        user.value.preferences = {
            ...(user.value.preferences ?? {}),
            seen_hints: updated,
        };
        await patchPreferences({ seen_hints: updated });
    }

    async function resetHints() {
        await patchPreferences({ seen_hints: [] });
    }

    async function setHintsEnabled(value: boolean) {
        if (user.value) {
            user.value.preferences = {
                ...(user.value.preferences ?? {}),
                hints_enabled: value,
            };
        }
        await patchPreferences({ hints_enabled: value });
    }

    return {
        user,
        currentOrganizationRole,
        currentOrganizationRoles,
        currentOrganizationPermissions,
        reset,
        photoUrl: computed(() => user.value?.profile_picture || ""),
        hintsEnabled,
        seenHints,
        markHintSeen,
        resetHints,
        setHintsEnabled,
    };
});

export const useUserManager = () => storeToRefs(useUserStore());
